import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ChevronDown, AlertCircle, MoreHorizontal, Trash2, RotateCcw, Loader2, Clock, User } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import WorkflowService from "../../services/workflow.service";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useAuth } from "../../useAuth";
import useToast from "../../hooks/useToast";
import ToastContainer from "../ui/ToastContainer";

const HealthBar = ({ percentage, totalRuns }) => {
  const getColor = (pct) => {
    if (pct >= 95) return "from-emerald-500 to-emerald-400";
    if (pct >= 85) return "from-yellow-500 to-yellow-400";
    if (pct >= 70) return "from-orange-500 to-orange-400";
    return "from-red-500 to-red-400";
  };

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden backdrop-blur-sm border border-white/10">
        <div
          className={`h-full bg-gradient-to-r ${getColor(percentage)} transition-all duration-500 shadow-lg`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs font-semibold text-white tabular-nums min-w-[2.5rem] text-right">
          {percentage}%
        </span>
        <span className="text-xs text-gray-500 tabular-nums">
          {totalRuns.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const variants = {
    active: {
      label: "Active",
      className: "bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 text-emerald-400 border-emerald-500/40 shadow-lg shadow-emerald-500/10"
    },
    disabled: {
      label: "Disabled",
      className: "bg-gradient-to-r from-gray-500/20 to-gray-600/20 text-gray-400 border-gray-500/40"
    },
    draft: {
      label: "Draft",
      className: "bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 border-blue-500/40 shadow-lg shadow-blue-500/10"
    },
    archived: {
      label: "Archived",
      className: "bg-gradient-to-r from-gray-600/20 to-gray-700/20 text-gray-500 border-gray-600/40"
    },
  };

  const variant = variants[status] || variants.draft;

  return (
    <Badge variant="outline" className={`text-[10px] font-semibold border backdrop-blur-sm ${variant.className}`}>
      {variant.label}
    </Badge>
  );
};

const VersionBadge = ({ version, isDraft }) => {
  if (!version && !isDraft) {
    return (
      <span className="text-xs font-medium text-gray-600 bg-gray-800/30 px-2 py-1 rounded-md backdrop-blur-sm">
        Unpublished
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-gray-300 bg-white/5 px-2 py-1 rounded-md backdrop-blur-sm border border-white/10">
        v{version || 0}
      </span>
      {isDraft && (
        <Badge variant="outline" className="text-[10px] font-semibold border bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 text-yellow-400 border-yellow-500/40 shadow-lg shadow-yellow-500/10 backdrop-blur-sm">
          draft
        </Badge>
      )}
    </div>
  );
};

// Los mínimos de esta rejilla suman 1.028px, más 144 de separación: 1.172px
// para poder dibujar una fila. En un teléfono de 390 eso es tres pantallas de
// scroll lateral por cada workflow, así que por debajo de `md` la fila no es
// una fila — es una tarjeta. Los datos son los mismos y las acciones también.
const GRID_COLS = "grid-cols-[minmax(280px,2fr),minmax(100px,1fr),minmax(100px,1fr),minmax(280px,1.5fr),minmax(100px,1fr),minmax(120px,1fr),48px]";

const WorkflowCard = ({ workflow, onClick, onDelete, onToggle, toggling }) => {
  const isDraft = workflow.status === 'draft';
  const canToggle = workflow.status === 'active' || workflow.status === 'disabled';

  return (
    <div className="border-b border-white/5 last:border-b-0 md:hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onClick(workflow.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(workflow.id); }
        }}
        className="cursor-pointer p-4 transition-colors active:bg-violet-500/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-400/60"
      >
        <div className="flex items-start gap-2">
          <h4 className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{workflow.name}</h4>
          <div className="shrink-0"><StatusBadge status={workflow.status} /></div>
        </div>

        {workflow.description && (
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500">{workflow.description}</p>
        )}

        {/* La versión y la salud comparten renglón: son las dos respuestas a
            "¿en qué estado está esto?" y por separado ocupaban dos filas para
            decir menos que juntas. */}
        <div className="mt-3 flex items-center gap-2.5">
          <VersionBadge version={workflow.published_version_number} isDraft={workflow.has_draft} />
          <span aria-hidden="true" className="h-4 w-px shrink-0 bg-white/10" />
          <span className="shrink-0 text-xs text-gray-500">Health</span>
          <div className="min-w-0 flex-1">
            <HealthBar percentage={workflow.health_percentage} totalRuns={workflow.total_runs} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 border-t border-white/5 px-4 py-2.5">
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-gray-400">
          <User className="h-3.5 w-3.5 shrink-0 text-gray-600" />
          <span className="truncate">{workflow.created_by}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-gray-400">
          <Clock className="h-3.5 w-3.5 shrink-0 text-gray-600" />
          {workflow.last_edit_time}
        </span>

        {/* El interruptor no está en la maqueta, pero es la única forma de
            activar o pausar un workflow sin entrar en él: quitarlo del móvil
            sería quitar la acción, no moverla. */}
        <div className="ml-auto flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {(canToggle || isDraft) && (
            <Switch
              checked={workflow.status === 'active'}
              loading={toggling}
              disabled={isDraft}
              onChange={() => onToggle(workflow)}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label={`Actions for ${workflow.name}`}
                className="rounded-lg p-2.5 text-gray-500 transition-all hover:bg-white/10 hover:text-gray-300"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-black/90 border border-gray-800 rounded-xl">
              <DropdownMenuItem
                className="hover:bg-red-900/50 text-red-400 cursor-pointer"
                onClick={() => onDelete(workflow)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Move to Trash
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const WorkflowRow = ({ workflow, onClick, onDelete, onToggle, toggling }) => {
  const isDraft = workflow.status === 'draft';
  const canToggle = workflow.status === 'active' || workflow.status === 'disabled';

  return (
    <>
    <WorkflowCard
      workflow={workflow}
      onClick={onClick}
      onDelete={onDelete}
      onToggle={onToggle}
      toggling={toggling}
    />
    <div className={`hidden md:grid ${GRID_COLS} gap-6 items-center px-6 py-4 border-b border-white/5 hover:bg-gradient-to-r hover:from-violet-500/5 hover:to-purple-500/5 cursor-pointer transition-all duration-300 group backdrop-blur-sm`}>
      <div className="min-w-0" onClick={() => onClick(workflow.id)}>
        <h4 className="text-sm font-semibold text-white mb-1 truncate group-hover:text-violet-300 transition-colors">
          {workflow.name}
        </h4>
        <p className="text-xs text-gray-500 truncate leading-relaxed">{workflow.description}</p>
      </div>

      <div className="flex items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
        {(canToggle || isDraft) && (
          <Switch
            checked={workflow.status === 'active'}
            loading={toggling}
            disabled={isDraft}
            onChange={() => onToggle(workflow)}
          />
        )}
        <div onClick={() => onClick(workflow.id)}>
          <StatusBadge status={workflow.status} />
        </div>
      </div>

      <div className="flex items-center" onClick={() => onClick(workflow.id)}>
        <VersionBadge version={workflow.published_version_number} isDraft={workflow.has_draft} />
      </div>

      <div className="min-w-0" onClick={() => onClick(workflow.id)}>
        <HealthBar percentage={workflow.health_percentage} totalRuns={workflow.total_runs} />
      </div>

      <div className="flex items-center" onClick={() => onClick(workflow.id)}>
        <span className="text-xs font-medium text-gray-400">{workflow.created_by}</span>
      </div>

      <div className="flex flex-col items-start" onClick={() => onClick(workflow.id)}>
        <div className="text-xs font-medium text-gray-400">{workflow.last_edit_time}</div>
        <div className="text-[10px] text-gray-600 mt-0.5">by {workflow.last_edit_by}</div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded-lg text-gray-600 hover:text-gray-300 hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-all">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-black/90 border border-gray-800 rounded-xl">
            <DropdownMenuItem
              className="hover:bg-red-900/50 text-red-400 cursor-pointer"
              onClick={() => onDelete(workflow)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Move to Trash
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    </>
  );
};

const TrashedWorkflowRow = ({ workflow, onRestore, restoring }) => {
  return (
    <>
    {/* Papelera en móvil: nombre, cuándo se borró y restaurar. El resto de
        columnas describen un workflow que ya no está en uso. */}
    <div className="flex items-center gap-3 border-b border-white/5 px-4 py-3 opacity-60 md:hidden">
      <div className="min-w-0 flex-1">
        <h4 className="truncate text-sm font-semibold text-gray-400">{workflow.name}</h4>
        <p className="truncate text-xs text-gray-600">
          Deleted {workflow.deleted_at ? new Date(workflow.deleted_at).toLocaleDateString() : '—'}
        </p>
      </div>
      <button
        onClick={() => onRestore(workflow)}
        disabled={restoring}
        aria-label={`Restore ${workflow.name}`}
        className="shrink-0 rounded-lg p-2 text-violet-400 transition-all hover:bg-violet-500/10 hover:text-violet-300 disabled:opacity-50"
      >
        {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
      </button>
    </div>

    <div className={`hidden md:grid ${GRID_COLS} gap-6 items-center px-6 py-4 border-b border-white/5 opacity-60 backdrop-blur-sm`}>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-gray-400 mb-1 truncate">{workflow.name}</h4>
        <p className="text-xs text-gray-600 truncate leading-relaxed">{workflow.description}</p>
      </div>

      <div className="flex items-center">
        <StatusBadge status={workflow.status} />
      </div>

      <div className="flex items-center">
        <VersionBadge version={workflow.published_version_number} isDraft={workflow.has_draft} />
      </div>

      <div className="text-xs text-gray-600">
        Deleted {workflow.deleted_at ? new Date(workflow.deleted_at).toLocaleDateString() : '—'}
      </div>

      <div className="flex items-center">
        <span className="text-xs font-medium text-gray-600">{workflow.created_by}</span>
      </div>

      <div className="flex flex-col items-start">
        <div className="text-xs font-medium text-gray-600">{workflow.last_edit_time}</div>
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={() => onRestore(workflow)}
          disabled={restoring}
          className="p-1.5 rounded-lg text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 transition-all disabled:opacity-50"
          title="Restore"
        >
          {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        </button>
      </div>
    </div>
    </>
  );
};

export default function WorkflowsList({ projectId, onSelectWorkflow }) {
  const navigate = useNavigate();
  const { currentAccount } = useOrganizations();
  const { user } = useAuth();
  const { toasts, removeToast, success: toastSuccess, error: toastError } = useToast();

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [creatorFilter] = useState("anyone");

  // Toggle (activate / deactivate) state
  const [activatingWorkflow, setActivatingWorkflow] = useState(null);

  // Trash state
  const [trashedWorkflows, setTrashedWorkflows] = useState([]);
  const [trashedLoading, setTrashedLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingWorkflow, setDeletingWorkflow] = useState(null);
  const [restoringWorkflow, setRestoringWorkflow] = useState(null);

  const extractUserName = (u) => {
    if (!u) return 'Unknown';
    if (typeof u === 'string') return u;
    return u.name || u.username || 'Unknown';
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return date.toLocaleDateString();
  };

  const transformWorkflow = (wf) => ({
    id: wf.workflow_id || wf.id,
    name: wf.name,
    description: wf.description || '',
    status: wf.status,
    published_version_number: wf.published_version_number,
    has_draft: wf.has_draft_changes || false,
    health_percentage: wf.stats?.success_rate ?? 0,
    total_runs: wf.total_runs || 0,
    created_by: extractUserName(wf.created_by),
    last_edit_time: formatRelativeTime(wf.updated_at),
    last_edit_by: extractUserName(wf.updated_by || wf.created_by),
    deleted_at: wf.workflow_metadata?.deleted_at || wf.deleted_at || null,
    is_deleted: wf.workflow_metadata?.is_deleted || wf.is_deleted || false
  });

  const handleToggleWorkflow = async (workflow) => {
    if (!currentAccount?.id || !projectId) return;
    if (workflow.status === 'draft') return; // draft workflows can't be activated directly
    const isActive = workflow.status === 'active';
    setActivatingWorkflow(workflow.id);
    // Optimistic update
    setWorkflows(prev =>
      prev.map(w => w.id === workflow.id ? { ...w, status: isActive ? 'disabled' : 'active' } : w)
    );
    try {
      const payload = { account_id: currentAccount.id, project_id: projectId };
      const result = isActive
        ? await WorkflowService.deactivateWorkflow(workflow.id, payload)
        : await WorkflowService.activateWorkflow(workflow.id, payload);
      if (!result.success) {
        // Roll back on failure
        setWorkflows(prev =>
          prev.map(w => w.id === workflow.id ? { ...w, status: workflow.status } : w)
        );
        toastError(result.error || 'Failed to update workflow status');
      }
    } catch (err) {
      setWorkflows(prev =>
        prev.map(w => w.id === workflow.id ? { ...w, status: workflow.status } : w)
      );
      toastError('Failed to update workflow status');
    } finally {
      setActivatingWorkflow(null);
    }
  };

  const fetchWorkflows = useCallback(async () => {
    if (!projectId || !currentAccount?.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await WorkflowService.listWorkflows({
        account_id: currentAccount.id,
        project_id: projectId,
        limit: 100,
        sort_by: 'updated_at',
        descending: true,
        include_definition: false
      });
      if (result.success) {
        setWorkflows((result.data.workflows || []).map(transformWorkflow));
      } else {
        setError(result.error || 'Failed to load workflows');
      }
    } catch (err) {
      setError(err.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentAccount?.id]);

  const fetchTrashedWorkflows = useCallback(async () => {
    if (!projectId || !currentAccount?.id) return;
    setTrashedLoading(true);
    try {
      const result = await WorkflowService.listWorkflows({
        account_id: currentAccount.id,
        project_id: projectId,
        limit: 100,
        sort_by: 'updated_at',
        descending: true,
        include_definition: false,
        include_soft_deleted: true
      });
      if (result.success) {
        const all = (result.data.workflows || []).map(transformWorkflow);
        setTrashedWorkflows(all.filter(w => w.is_deleted || w.deleted_at || w.status === 'archived'));
      }
    } catch (err) {
      console.error('Failed to load trashed workflows:', err);
    } finally {
      setTrashedLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentAccount?.id]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'trash') fetchTrashedWorkflows();
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeletingWorkflow(deleteConfirm.id);
    try {
      const result = await WorkflowService.deleteWorkflow(deleteConfirm.id, {
        account_id: currentAccount?.id,
        project_id: projectId,
        user_id: user?.user_id
      });
      if (result.success) {
        setWorkflows(prev => prev.filter(w => w.id !== deleteConfirm.id));
        toastSuccess(`"${deleteConfirm.name}" moved to Trash`);
      } else {
        toastError('Failed to move workflow to Trash. Please try again.');
      }
    } finally {
      setDeletingWorkflow(null);
      setDeleteConfirm(null);
    }
  };

  const handleRestore = async (workflow) => {
    setRestoringWorkflow(workflow.id);
    try {
      const result = await WorkflowService.restoreWorkflow(workflow.id, {
        account_id: currentAccount?.id,
        project_id: projectId,
      });
      if (result.success) {
        setTrashedWorkflows(prev => prev.filter(w => w.id !== workflow.id));
        await fetchWorkflows();
        toastSuccess(`"${workflow.name}" restored successfully`);
      } else {
        toastError('Failed to restore workflow. Please try again.');
      }
    } finally {
      setRestoringWorkflow(null);
    }
  };

  const filteredWorkflows = workflows.filter(wf => {
    if (activeTab === "active" && wf.status !== "active") return false;
    if (activeTab === "disabled" && wf.status !== "disabled") return false;
    if (activeTab === "drafts" && wf.status !== "draft") return false;
    if (searchTerm && !wf.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (creatorFilter !== "anyone" && wf.created_by !== creatorFilter) return false;
    return true;
  });

  const counts = {
    all: workflows.length,
    active: workflows.filter(w => w.status === "active").length,
    disabled: workflows.filter(w => w.status === "disabled").length,
    drafts: workflows.filter(w => w.status === "draft").length,
  };

  const isTrashTab = activeTab === "trash";

  return (
    <div className="h-full">
      <div className="mb-6 flex flex-col gap-4 md:mb-8 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent md:text-3xl">
            Workflows
          </h2>
          <p className="text-sm text-gray-500 mt-2 max-w-2xl leading-relaxed">
            Every workflow definition in the workspace. State, composition and health at a glance — no DAG drawing needed.
          </p>
        </div>
        {/* A lo ancho en móvil, y con su nombre: es la única acción de la
            pantalla y no compite con nada por el sitio. */}
        <Button
          onClick={() => onSelectWorkflow ? onSelectWorkflow(null) : navigate(`/workflow/builder?projectId=${projectId}`)}
          className="w-full shrink-0 bg-primary text-white transition-colors hover:bg-violet-700 md:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          New workflow
        </Button>
      </div>

      {/* Cinco pestañas con sus contadores piden ~430px: en móvil van en su
          propia fila y se desplazan, en vez de comerse la búsqueda. */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <div className="flex shrink-0 gap-1 overflow-x-auto rounded-xl border border-white/10 bg-black/40 p-1 shadow-2xl backdrop-blur-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {["all", "active", "disabled", "drafts"].map(tab => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition-all duration-300 md:px-4 ${
                activeTab === tab
                  ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/30"
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab ? "bg-white/20" : "bg-white/10"}`}>
                {counts[tab]}
              </span>
            </button>
          ))}

          {/* Trash tab */}
          <button
            onClick={() => handleTabChange("trash")}
            className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition-all duration-300 md:px-4 ${
              isTrashTab
                ? "bg-gradient-to-r from-red-700 to-red-600 text-white shadow-lg shadow-red-500/20"
                : "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
            }`}
          >
            <Trash2 className="w-3 h-3" />
            Trash
          </button>
        </div>

        {!isTrashTab && (
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1 md:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 text-sm bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/20 transition-all shadow-xl"
              />
            </div>
            <button className="flex w-full shrink-0 items-center justify-between gap-2 rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-xs font-medium text-gray-300 shadow-xl backdrop-blur-xl transition-all hover:border-violet-500/30 hover:bg-white/5 md:w-auto md:justify-start">
              Created by: Anyone
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {isTrashTab && (
          <p className="text-xs text-gray-500">
            Deleted workflows are kept here and can be restored at any time.
          </p>
        )}
      </div>

      <div className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        {/* Table Header */}
        <div className={`hidden md:grid ${GRID_COLS} gap-6 px-6 py-4 bg-gradient-to-r from-white/5 to-white/[0.02] border-b border-white/10 backdrop-blur-xl`}>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Workflow</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Version</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {isTrashTab ? "Deleted" : "Health - Last 30 Days"}
          </div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Created by</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Last edit</div>
          <div />
        </div>

        {/* Table Body */}
        <div className="divide-y divide-white/5">
          {isTrashTab ? (
            trashedLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  <div className="text-sm text-gray-500 font-medium">Loading trash...</div>
                </div>
              </div>
            ) : trashedWorkflows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Trash2 className="w-10 h-10 text-gray-700 mb-3" />
                <div className="text-sm text-gray-500 font-medium">Trash is empty</div>
                <p className="text-xs text-gray-600 mt-2">Deleted workflows will appear here</p>
              </div>
            ) : (
              trashedWorkflows.map(workflow => (
                <TrashedWorkflowRow
                  key={workflow.id}
                  workflow={workflow}
                  onRestore={handleRestore}
                  restoring={restoringWorkflow === workflow.id}
                />
              ))
            )
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex items-center gap-3 text-red-400 mb-3">
                <AlertCircle className="w-5 h-5" />
                <div className="text-sm font-medium">Failed to load workflows</div>
              </div>
              <p className="text-xs text-gray-500">{error}</p>
              <Button onClick={fetchWorkflows} className="mt-4 bg-primary hover:bg-violet-700">
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                <div className="text-sm text-gray-500 font-medium">Loading workflows...</div>
              </div>
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-sm text-gray-500 font-medium">No workflows found</div>
              <p className="text-xs text-gray-600 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            filteredWorkflows.map(workflow => (
              <WorkflowRow
                key={workflow.id}
                workflow={workflow}
                onClick={(id) => onSelectWorkflow
                  ? onSelectWorkflow(id, {
                      version: workflow.published_version_number,
                      status: workflow.status,
                      hasDraft: workflow.has_draft
                    })
                  : navigate(`/workflows/${id}/editor`)}
                onDelete={setDeleteConfirm}
                onToggle={handleToggleWorkflow}
                toggling={activatingWorkflow === workflow.id}
              />
            ))
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => !deletingWorkflow && setDeleteConfirm(null)}
        >
          <div
            className="bg-gray-950 border border-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-900/30">
                <Trash2 className="w-5 h-5 text-red-400" />
              </span>
              <div>
                <h3 className="text-lg font-bold text-white">Move to Trash</h3>
                <p className="text-sm text-gray-400">This action can be undone from the Trash tab.</p>
              </div>
            </div>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-white">"{deleteConfirm.name}"</span>?
              It will be moved to the trash and can be restored later.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
                onClick={() => setDeleteConfirm(null)}
                disabled={!!deletingWorkflow}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-500 text-white"
                onClick={handleConfirmDelete}
                disabled={!!deletingWorkflow}
              >
                {deletingWorkflow ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Move to Trash</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
}
